import { Close } from '@mui/icons-material'
import {
  Divider,
  Fade,
  Modal,
  Typography,
  alpha,
  keyframes,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import React, { PropsWithChildren } from 'react'
import { colors } from '../../constants/colors'
import { BORDER_RADIUS_XL, MODAL_WIDTH_MEDIUM } from '../../constants/sizes'
import { CircleButton, HBox, VBox } from '../../elements/basic-elements'
import PartialScreenModal from '../partial-screen-modal'

type TopDrawerModalProps = PropsWithChildren<{
  open: boolean
  onClose: () => void
  /** Renders a centered title row with a divider under it */
  title?: string
}>

/**
 * Drawer that drops down from the top of the screen: a floating round close
 * button with a fully rounded card beneath it, over a soft page-colored
 * backdrop. Built for mobile trays (notifications and their settings).
 */
const TopDrawerModal = ({ open, onClose, title, children }: TopDrawerModalProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      aria-labelledby={title ? 'top-drawer-title' : undefined}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(isDark ? colors.DARK_MODE_100 : colors.BLACK_10, 0.94)
          }
        }
      }}
    >
      <Fade in={open} timeout={{ enter: 320, exit: 200 }}>
        <DrawerContainer
          sx={{
            // Subtle rise into place on open, sink back slightly on close
            animation: `${riseIn} 320ms cubic-bezier(0, 0, 0, 1)`,
            transform: open ? 'none' : `translateY(${RISE_DISTANCE}px)`,
            transition: 'transform 200ms ease-in'
          }}
        >
          <CircleButton
            variant="contained"
            color="tertiary"
            disableElevation
            onClick={onClose}
            aria-label="Close"
            sx={{ pointerEvents: 'all' }}
          >
            <Close sx={{ color: 'text.primary' }} />
          </CircleButton>
          <DrawerCard>
            {title && (
              <>
                <HBox px={2.5} py={2.5} justifyContent="center">
                  <Typography
                    id="top-drawer-title"
                    variant="h5"
                    color="textPrimary"
                    textAlign="center"
                  >
                    {title}
                  </Typography>
                </HBox>
                <Divider />
              </>
            )}
            {children}
          </DrawerCard>
        </DrawerContainer>
      </Fade>
    </Modal>
  )
}

const CLOSE_BUTTON_SIZE = 46
const RISE_DISTANCE = 16

const riseIn = keyframes`
  from {
    transform: translateY(${RISE_DISTANCE}px);
  }
  to {
    transform: none;
  }
`

const DrawerContainer = styled(VBox)`
  ${({ theme }) => `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1.5)};
    pointer-events: none;
    outline: none;
  `}
`

const DrawerCard = styled(VBox)`
  ${({ theme }) => `
    pointer-events: all;
    width: 100%;
    max-width: ${MODAL_WIDTH_MEDIUM}px;
    max-height: calc(100dvh - ${CLOSE_BUTTON_SIZE}px - ${theme.spacing(4.5)});
    overflow-y: auto;
    border-radius: ${BORDER_RADIUS_XL}px;
    background: ${theme.palette.tertiary.main};
    box-shadow: 0 12px 40px -16px rgba(0, 0, 0, 0.25);
  `}
`

/**
 * Tray modal that uses the top drawer on phones and the compact right-side
 * panel on larger screens.
 */
export const ResponsiveTrayModal = ({ open, onClose, title, children }: TopDrawerModalProps) => {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))

  return isPhone ? (
    <TopDrawerModal open={open} onClose={onClose} title={title}>
      {children}
    </TopDrawerModal>
  ) : (
    <PartialScreenModal
      open={open}
      onClose={onClose}
      compact
      borderRadius={BORDER_RADIUS_XL}
      title={title}
      containerProps={{ maxHeight: 'inherit', height: '100%' }}
    >
      {children}
    </PartialScreenModal>
  )
}

export default TopDrawerModal
